/**
 * services/TradeService.ts
 * Service for Trade — the strategic setup record created on draft promotion.
 *
 * Trade records are created once (at the promotion boundary) and updated as the
 * trading workflow progresses. Execution details live on TradeExecution/TradeExecutionEvent;
 * this service owns only the strategy and lifecycle state of the setup.
 *
 * Write paths:
 *   - create()              — called from TradePromotionService (T07) inside a
 *                             caller-supplied Dexie transaction (promotion boundary)
 *   - updateWorkflowStatus() — standalone user action; opens its own transaction
 *   - updateShelved()        — standalone user action; opens its own transaction
 *
 * Key lookup:
 *   - getByRootDraftId()    — drawer mode lookup: determines whether a draft has a
 *                             promoted Trade (persistent-trade mode) or not (draft mode)
 */

import { BaseService } from './BaseService';
import { isTombstoned } from '@/utils/tombstone';
import { generateUUID } from '@/utils/uuid';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { getSyncOperationService } from './sync/SyncOperationService';
import { buildSetOps } from './sync/OperationBuilder';
import type { Trade } from '@/types/schema/Trade';
import type {
  StrategySnapshot,
  PlaybookSnapshot,
  CriteriaSnapshot,
  ConfluenceSnapshot,
  EntryModelSnapshot,
} from '@/types/shared/StrategySnapshots';

// ── Workflow Transition State Machine ─────────────────────────────────────────

/**
 * Valid workflow status transitions for a Trade.
 *
 * Terminal states map to an empty array — any transition attempt throws.
 * Backward revert ('ready-for-review' → 'active') is explicitly accepted.
 *
 * @see TradeService.updateWorkflowStatus — enforces this map at the service layer (T03)
 */
const VALID_WORKFLOW_TRANSITIONS: Readonly<Record<Trade['workflowStatus'], readonly Trade['workflowStatus'][]>> = {
  'active':           ['ready-for-review'],
  'ready-for-review': ['complete', 'active'],
  'complete':         [],
};

// ── Input Types ───────────────────────────────────────────────────────────────

/**
 * Input for creating a Trade during draft promotion.
 *
 * workflowStatus and isShelved are always set to their promotion defaults
 * ('active', false) by the service — callers cannot override them here.
 * Update them post-promotion via updateWorkflowStatus() / updateShelved().
 */
export interface CreateTradeInput {
  /** The specific draft instance that was promoted (for audit) */
  sourceDraftId: string;
  /**
   * Stable lineage root key — used as the unique index for promotion idempotency
   * and drawer mode lookup. Set to the original draft's id on first creation;
   * inherited unchanged on carry-forward.
   */
  rootDraftId: string;
  /** Session in which the promotion occurred */
  sessionId?: string;
  /** Session in which the trade setup was first captured */
  setupSessionId?: string;
  /** Strategy snapshot from the draft at promotion time */
  strategySnapshot?: StrategySnapshot;
  /** Playbook snapshot from the draft at promotion time */
  playbookSnapshot?: PlaybookSnapshot;
  /** Criteria evaluation snapshot */
  criteriaSnapshot?: CriteriaSnapshot[];
  /** Confluence snapshot */
  confluenceSnapshot?: ConfluenceSnapshot[];
  /** Entry model snapshot */
  entryModelSnapshot?: EntryModelSnapshot[];
  /** Notes carried from the draft */
  notes?: Trade['notes'];
  /** Images carried from the draft */
  images?: Trade['images'];
}

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * Service for Trade entities.
 *
 * Inherits get(), getAll(), softDelete() from BaseService for sync/tombstone flows.
 */
export class TradeService extends BaseService<Trade> {
  constructor() {
    super('trades');
  }

  // ===== PROMOTION WRITE =====

  /**
   * Create a Trade record during draft promotion.
   *
   * IMPORTANT: Must be called inside an active Dexie 'rw' transaction that
   * includes db.trades, db.sync_operations, and db.sync_state.
   * Dexie auto-propagates the active transaction.
   *
   * Idempotency: if a non-tombstoned Trade already exists with this rootDraftId
   * (unique index), it is returned without writing a duplicate.
   *
   * Promotion defaults: workflowStatus = 'active', isShelved = false.
   *
   * @param data - Trade fields supplied by the promotion caller
   * @returns The created Trade, or the existing one if already promoted
   */
  async create(data: CreateTradeInput): Promise<Trade> {
    // Idempotency: unique index on rootDraftId — at most one live Trade per lineage
    const existing = await this.table
      .where('rootDraftId')
      .equals(data.rootDraftId)
      .filter(t => !isTombstoned(t))
      .first();
    if (existing) {
      return existing;
    }

    const now = new Date().toISOString();
    const trade: Trade = {
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
      sourceDraftId: data.sourceDraftId,
      rootDraftId: data.rootDraftId,
      workflowStatus: 'active',
      isShelved: false,
      ...(data.sessionId !== undefined && { sessionId: data.sessionId }),
      ...(data.setupSessionId !== undefined && { setupSessionId: data.setupSessionId }),
      ...(data.strategySnapshot !== undefined && { strategySnapshot: data.strategySnapshot }),
      ...(data.playbookSnapshot !== undefined && { playbookSnapshot: data.playbookSnapshot }),
      ...(data.criteriaSnapshot !== undefined && { criteriaSnapshot: data.criteriaSnapshot }),
      ...(data.confluenceSnapshot !== undefined && { confluenceSnapshot: data.confluenceSnapshot }),
      ...(data.entryModelSnapshot !== undefined && { entryModelSnapshot: data.entryModelSnapshot }),
      ...(data.notes !== undefined && { notes: data.notes }),
      ...(data.images !== undefined && { images: data.images }),
    };

    markAsInstrumentedWriteContext();
    await this.table.add(trade);

    const ops = buildSetOps(
      'trades',
      trade.id,
      trade as unknown as Record<string, unknown>
    );
    if (ops.length > 0) {
      await getSyncOperationService().logBatch(ops);
    }

    return trade;
  }

  // ===== WORKFLOW UPDATES =====

  /**
   * Update the workflow lifecycle status of a Trade.
   *
   * Standalone user action — opens its own transaction via BaseService.update().
   * Enforces the VALID_WORKFLOW_TRANSITIONS state machine (T03, WU2D):
   *   active           → ready-for-review
   *   ready-for-review → complete | active  (backward revert allowed)
   *   complete         → (terminal — any transition throws)
   *
   * @param id - Trade ID
   * @param status - New workflow status
   * @returns Updated Trade record
   * @throws If the trade is not found or the transition is not valid
   */
  async updateWorkflowStatus(
    id: string,
    status: Trade['workflowStatus']
  ): Promise<Trade> {
    const current = await this.get(id);
    if (!current) {
      throw new Error(`Trade ${id} not found.`);
    }
    if (!VALID_WORKFLOW_TRANSITIONS[current.workflowStatus].includes(status)) {
      throw new Error(
        `Invalid workflow transition: '${current.workflowStatus}' → '${status}'.`
      );
    }
    return this.update(id, { workflowStatus: status });
  }

  /**
   * Update the shelved state of a Trade.
   *
   * Standalone user action — opens its own transaction via BaseService.update().
   * Shelving is orthogonal to workflowStatus — a trade can be shelved at any stage.
   *
   * @param id - Trade ID
   * @param isShelved - New shelved state
   * @returns Updated Trade record
   */
  async updateShelved(id: string, isShelved: boolean): Promise<Trade> {
    return this.update(id, { isShelved });
  }

  /**
   * Persist image references to a Trade (persistent-trade mode image durability).
   *
   * Called fire-and-forget from NotesSection on every add/delete in persistent-trade mode.
   * Replaces the entire images object — caller supplies the full updated state.
   *
   * @param id - Trade ID
   * @param images - Full updated images object
   * @returns Updated Trade record
   */
  async updateImages(id: string, images: NonNullable<Trade['images']>): Promise<Trade> {
    return this.update(id, { images });
  }

  // ===== QUERIES =====

  /**
   * Get all non-tombstoned Trades for a session.
   *
   * Uses the sessionId index.
   *
   * @param sessionId - Session ID
   * @returns Array of Trades for the session
   */
  async getBySessionId(sessionId: string): Promise<Trade[]> {
    try {
      const records = await this.table
        .where('sessionId')
        .equals(sessionId)
        .toArray();
      return records.filter(t => !isTombstoned(t));
    } catch (error) {
      console.error(`Failed to get trades for session ${sessionId}:`, error);
      throw new Error('Failed to retrieve trades');
    }
  }

  /**
   * Look up a Trade by rootDraftId.
   *
   * Used by the drawer to determine mode:
   *   - Returns a Trade → persistent-trade mode (draft has been promoted)
   *   - Returns undefined → draft mode (no promotion yet)
   *
   * rootDraftId is a unique index — at most one live Trade per draft lineage.
   *
   * @param rootDraftId - Stable lineage key from TradeDraftBase
   * @returns The Trade for this lineage, or undefined if not yet promoted
   */
  async getByRootDraftId(rootDraftId: string): Promise<Trade | undefined> {
    try {
      const record = await this.table
        .where('rootDraftId')
        .equals(rootDraftId)
        .filter(t => !isTombstoned(t))
        .first();
      return record;
    } catch (error) {
      console.error(`Failed to get trade for rootDraftId ${rootDraftId}:`, error);
      throw new Error('Failed to retrieve trade');
    }
  }
}

// Export singleton instance
export const tradeService = new TradeService();
